import {
	AppButton,
	ChangePlanModal,
	type ChangePlanPlan
} from '@clickguard/design-system'
import {
	DiscountInfoDTO,
	PaymentMethodDTO,
	PricingTierPriceInfoDTO
} from 'api-models'
import { NewPaymentMethod } from 'components/new-payment-method'
import { useStores } from 'hooks'
import { usePostHog } from 'posthog-js/react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { formatCurrency } from 'utils'
import { parseError } from 'utils/errors'
import { useUiStore } from 'store'
import { useBlackFridaySelection } from 'hooks/use-black-friday-selection'
import { NewSubscriptionBenefits } from 'components/black-friday-banner/new-subscription-benefits'
import { NewSubscriptionBanner } from 'components/black-friday-banner'
import {
	BillingPeriod,
	buildTierPlans,
	CONFIG_FIELDS_FIX,
	configFieldsFixCss,
	couponErrorMessage,
	CYCLES,
	FEATURES,
	getBasePrice,
	getPrices,
	getSubscriptionBillingPeriod,
	PERIOD_TO_COUNT,
	PERIOD_TO_INTERVAL
} from './plan-modal-helpers'

export const NewSubscriptionPlan = () => {
	const { accountStore, billingStore } = useStores()
	const { account, newSubscription } = accountStore
	const { createOfferRequest, paymentMethods, getPaymentMethods } = billingStore
	const posthog = usePostHog()
	const isLoading = useUiStore((s) => s.isLoading)

	const [open, setOpen] = useState(false)
	const [loading, setLoading] = useState(false)
	const [showNewPayment, setShowNewPayment] = useState(false)
	const [filteredPaymentMethods, setFilteredPaymentMethod] = useState<
		PaymentMethodDTO[] | null
	>(null)

	const [cycle, setCycle] = useState<BillingPeriod>('yearly')
	const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null)
	const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null)
	const [customSpend, setCustomSpend] = useState('')
	const [couponCode, setCouponCode] = useState('')
	const [coupon, setCoupon] = useState<DiscountInfoDTO | null>(null)
	const [couponError, setCouponError] = useState<string | undefined>(undefined)

	const plans = billingStore.plans

	useEffect(() => {
		billingStore.initBilling()
		getPaymentMethods()
	}, [])

	// Only cards belonging to this account are usable for its first subscription.
	useEffect(() => {
		const filteredMethods = account?.stripe?.stripe_customer_id
			? paymentMethods?.filter((pm) => pm?.account_id === account?.id)
			: paymentMethods
		setFilteredPaymentMethod(filteredMethods)
	}, [paymentMethods, account])

	const isCustom = selectedPlanId === 'custom'
	const selectedPlan = plans.find((p) => p.tier === selectedPlanId) || null
	const selectedPrice: PricingTierPriceInfoDTO | null = selectedPlan
		? getPrices(selectedPlan, cycle).find(
				(p) => p.price_id === selectedPriceId
		  ) || getBasePrice(selectedPlan, cycle)
		: null
	const campaign = useBlackFridaySelection({
		accountId: account?.id,
		tier: selectedPlanId,
		priceId: isCustom ? null : selectedPrice?.price_id,
		spend: isCustom ? Number(customSpend) : selectedPrice?.spend || 0,
		yearly: cycle === 'yearly',
		enabled: open,
		busy: loading || showNewPayment
	})
	useEffect(() => {
		if (campaign.active) {
			setCoupon(null)
			setCouponCode('')
		}
	}, [campaign.active])

	const selectPlan = (planId: string) => {
		if (loading || showNewPayment) return
		posthog.capture(`click-on-${planId}-plan`)
		if (planId === 'custom') {
			setSelectedPlanId('custom')
			setSelectedPriceId(null)
			return
		}
		const plan = plans.find((p) => p.tier === planId)
		if (!plan) return
		setSelectedPlanId(planId)
		setSelectedPriceId(getBasePrice(plan, cycle)?.price_id ?? null)
	}

	// Default-select the recommended plan once plans load (opens with Standard
	// expanded), matching the Change subscription modal.
	useEffect(() => {
		if (!open || selectedPlanId || !plans.length) return
		const recommended = plans.find((p) => p.tier === 'standard') || plans[0]
		selectPlan(recommended.tier)
	}, [open, plans, selectedPlanId])

	const changeCycle = (next: BillingPeriod) => {
		if (loading || showNewPayment) return
		posthog.capture(`click-on-${next}-plan`)
		setCycle(next)
		if (isCustom || !selectedPlan) return
		// Keep the same ad-spend tier across the period switch when possible.
		const current = selectedPlan.prices.find(
			(p) => p.price_id === selectedPriceId
		)
		const matched =
			getPrices(selectedPlan, next).find((p) => p.spend === current?.spend) ||
			getBasePrice(selectedPlan, next)
		setSelectedPriceId(matched?.price_id ?? null)
	}

	const claimCoupon = async (code: string) => {
		if (!code) return
		try {
			const discount = await billingStore.fetchCoupon(code, {
				billing_period: cycle,
				interval: PERIOD_TO_INTERVAL[cycle],
				interval_count: PERIOD_TO_COUNT[cycle],
				account_id: account?.id
			})
			setCoupon(discount)
			setCouponCode(code)
			setCouponError(undefined)
		} catch (err: any) {
			setCoupon(null)
			setCouponError(couponErrorMessage(err))
		}
	}

	const isSaving = () => isLoading('ACCOUNT_SAVE_SUBSCRIPTION') || loading
	const isReady = () => isCustom || !!selectedPrice

	// First subscription needs a card on file. If none exists yet, open the
	// Stripe payment-method dialog and finish once the card is authorized.
	const handleConfirm = async (pm?: string) => {
		if (isCustom || !selectedPlan || !selectedPrice || isSaving()) return

		if (filteredPaymentMethods?.length === 0 && !pm) {
			setShowNewPayment(true)
			return
		}

		setLoading(true)
		try {
			await newSubscription(account!.id, {
				tier: selectedPlan.tier,
				price_id: selectedPrice.price_id,
				coupon_code: coupon ? couponCode : null,
				pm_id: pm || null,
				black_friday_selection_id: campaign.selectionId
			})
			setOpen(false)
		} catch (err: any) {
			toast.error(parseError(err) || 'Failed to start subscription')
		} finally {
			setLoading(false)
		}
	}

	const handleQuote = async () => {
		await createOfferRequest({
			account_id: account!.id,
			spend: Number(customSpend) || 0,
			interval: cycle,
			notes: 'From new subscription dialog'
		})
		setOpen(false)
	}

	const uiPlans: ChangePlanPlan[] = useMemo(() => {
		const tierPlans = buildTierPlans({
			plans,
			selectedPlanId,
			selectedPrice,
			cycle,
			coupon
		})
		if (campaign.active && campaign.quote) {
			const selected = tierPlans.find((plan) => plan.id === selectedPlanId)
			const money = (amount: number) =>
				new Intl.NumberFormat(undefined, {
					style: 'currency',
					currency: campaign.quote!.currency.toUpperCase()
				}).format(amount / 100)
			if (selected) {
				selected.originalPrice = selected.price
				selected.price = `${money(
					campaign.quote.first_year_amount / 12
				)} /month`
				selected.priceCaption = `${money(
					campaign.quote.first_year_amount
				)} first year; ${money(
					campaign.quote.standard_annual_amount
				)} at renewal`
			}
		}

		tierPlans.push({
			id: 'custom',
			name: 'Custom',
			description: 'Request a custom plan',
			price: 'Custom pricing',
			priceCaption: 'Personalized quote',
			features: FEATURES.custom,
			customAdSpend: true,
			actions: {
				primaryAction: {
					label: 'Request quote',
					caption: 'Our team will send you an offer shortly',
					onClick: handleQuote,
					disabled: isSaving() || campaign.loading || !!campaign.error
				}
			}
		})

		return tierPlans
	}, [
		plans,
		selectedPlanId,
		selectedPriceId,
		cycle,
		coupon,
		loading,
		customSpend,
		campaign.active,
		campaign.quote,
		campaign.loading,
		campaign.error
	])

	const adSpendOptions =
		selectedPlan && !isCustom
			? getPrices(selectedPlan, cycle).map((p) => ({
					value: p.price_id,
					label: formatCurrency(p.spend, 'dollar')
			  }))
			: undefined

	return (
		<>
			<style>{configFieldsFixCss}</style>
			{/* Figma 1052:27560 — "Button (Domain)" primary; the 12px inset lands on
			    11px padding + 1px border to keep Figma's 36px outer box. */}
			<AppButton
				variant="primary"
				size="md"
				label="Start new plan"
				className="!px-[11px]"
				onClick={() => {
					setCycle(getSubscriptionBillingPeriod(account?.billing?.subscription))
					setSelectedPlanId(null)
					setSelectedPriceId(null)
					setCoupon(null)
					setCouponCode('')
					setCouponError(undefined)
					setOpen(true)
				}}
			/>

			<ChangePlanModal
				className={`${CONFIG_FIELDS_FIX} ${
					campaign.active ? '[&_button[aria-label="Remove_coupon"]]:hidden' : ''
				}`}
				open={open}
				onClose={() => !isSaving() && setOpen(false)}
				closeOnOverlayClick={!isSaving()}
				closeOnEscape={!isSaving()}
				title={
					<div className="flex flex-col gap-3">
						<NewSubscriptionBanner open={campaign.showBanner} />
						<span>New subscription</span>
					</div>
				}
				cycles={CYCLES}
				cycle={cycle}
				onCycleChange={(c) => changeCycle(c as BillingPeriod)}
				plans={uiPlans}
				selectedPlanId={selectedPlanId ?? undefined}
				onSelectPlan={selectPlan}
				adSpendOptions={adSpendOptions}
				adSpendValue={selectedPriceId ?? undefined}
				onAdSpendChange={(price) => {
					if (!loading && !showNewPayment) setSelectedPriceId(price)
				}}
				customAdSpendValue={customSpend}
				onCustomAdSpendChange={(spend) => {
					if (!loading && !showNewPayment) setCustomSpend(spend)
				}}
				onApplyCoupon={campaign.active ? undefined : claimCoupon}
				couponError={campaign.error || couponError}
				appliedCoupon={
					campaign.active ? 'BLACKFRIDAY' : coupon ? couponCode : undefined
				}
				couponAppliedText={
					campaign.active ? (
						<NewSubscriptionBenefits
							couponCode=""
							standardK={
								(isCustom ? Number(customSpend) : selectedPrice?.spend || 0) /
								1000
							}
							boostedK={
								((isCustom ? Number(customSpend) : selectedPrice?.spend || 0) *
									3) /
								1000
							}
							isCustomPlan={isCustom}
						/>
					) : undefined
				}
				onRemoveCoupon={
					campaign.active
						? undefined
						: () => {
								setCoupon(null)
								setCouponError(undefined)
						  }
				}
				primaryAction={{
					label: 'Start subscription',
					caption: 'New subscription will start immediately',
					onClick: () => handleConfirm(),
					disabled:
						!isReady() || isSaving() || campaign.loading || !!campaign.error
				}}
			/>

			{showNewPayment && (
				<NewPaymentMethod
					newWorkspace={true}
					show={showNewPayment}
					onClose={() => setShowNewPayment(false)}
					onSelect={(pm) => typeof pm === 'string' && handleConfirm(pm)}
				/>
			)}
		</>
	)
}
